import type { EvaluationRecordsResponse } from 'generated/leaderboard-client';

export type EvaluationNonFiniteFloatValue = 'NaN' | 'Infinity' | '-Infinity';

type NormalizedEvaluationRecordValueBase = {
  evaluationSeriesId: string;
  metricName: string;
  recordName: string;
  runId: string;
  sourceIndex: number;
};

export type NormalizedEvaluationRecordValue =
  | (NormalizedEvaluationRecordValueBase & {
      floatValue: number | EvaluationNonFiniteFloatValue;
      stringValue: null;
      type: 'FLOAT64';
    })
  | (NormalizedEvaluationRecordValueBase & {
      floatValue: null;
      stringValue: string;
      type: 'STRING';
    });

export type NormalizedEvaluationRecordsResponse = {
  records: NormalizedEvaluationRecordValue[];
  nextToken: string | null;
};

export class InvalidEvaluationRecordsResponseError extends Error {
  override name = 'InvalidEvaluationRecordsResponseError';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonFiniteFloatValue(value: unknown): value is EvaluationNonFiniteFloatValue {
  return value === 'NaN' || value === 'Infinity' || value === '-Infinity';
}

function invalidRecord(index: number, detail: string): InvalidEvaluationRecordsResponseError {
  return new InvalidEvaluationRecordsResponseError(
    `Invalid evaluation records response at records[${index}]: ${detail}.`,
  );
}

function normalizeEvaluationRecordValue(
  value: unknown,
  index: number,
): NormalizedEvaluationRecordValue {
  if (!isRecord(value)) {
    throw invalidRecord(index, 'expected an object');
  }

  const sourceIndex = value.sourceIndex;
  const runId = value.runId;
  const evaluationSeriesId = value.evaluationSeriesId;
  const recordName = value.recordName;
  const metricName = value.metricName;
  const type = value.type;

  if (typeof sourceIndex !== 'number' || !Number.isInteger(sourceIndex) || sourceIndex < 0) {
    throw invalidRecord(index, 'sourceIndex must be a non-negative integer');
  }

  if (typeof runId !== 'string' || runId.length === 0) {
    throw invalidRecord(index, 'runId must be a non-empty string');
  }

  if (typeof evaluationSeriesId !== 'string' || evaluationSeriesId.length === 0) {
    throw invalidRecord(index, 'evaluationSeriesId must be a non-empty string');
  }

  if (typeof recordName !== 'string') {
    throw invalidRecord(index, 'recordName must be a string');
  }

  if (typeof metricName !== 'string') {
    throw invalidRecord(index, 'metricName must be a string');
  }

  if (type === 'FLOAT64') {
    const floatValue = value.floatValue;

    if (
      !(
        (typeof floatValue === 'number' && Number.isFinite(floatValue)) ||
        isNonFiniteFloatValue(floatValue)
      )
    ) {
      throw invalidRecord(index, 'FLOAT64 floatValue must be finite or a non-finite marker');
    }

    if (value.stringValue !== null && value.stringValue !== undefined) {
      throw invalidRecord(index, 'FLOAT64 stringValue must be null');
    }

    return {
      evaluationSeriesId,
      floatValue,
      metricName,
      recordName,
      runId,
      sourceIndex,
      stringValue: null,
      type,
    };
  }

  if (type === 'STRING') {
    if (typeof value.stringValue !== 'string') {
      throw invalidRecord(index, 'STRING stringValue must be a string');
    }

    if (value.floatValue !== null && value.floatValue !== undefined) {
      throw invalidRecord(index, 'STRING floatValue must be null');
    }

    return {
      evaluationSeriesId,
      floatValue: null,
      metricName,
      recordName,
      runId,
      sourceIndex,
      stringValue: value.stringValue,
      type,
    };
  }

  throw invalidRecord(index, 'type must be FLOAT64 or STRING');
}

export function normalizeEvaluationRecordsResponse(
  value: EvaluationRecordsResponse,
): NormalizedEvaluationRecordsResponse {
  if (!isRecord(value) || !Array.isArray(value.records)) {
    throw new InvalidEvaluationRecordsResponseError(
      'Invalid evaluation records response: records must be an array.',
    );
  }

  if (
    value.nextToken !== null &&
    value.nextToken !== undefined &&
    typeof value.nextToken !== 'string'
  ) {
    throw new InvalidEvaluationRecordsResponseError(
      'Invalid evaluation records response: nextToken must be a string or null.',
    );
  }

  return {
    records: value.records.map(normalizeEvaluationRecordValue),
    nextToken: value.nextToken ?? null,
  };
}
